import { getSupabase, isSupabaseConfigured } from './client';
import { LEARNER_TABLES } from './tables';
import { calculateDailyStudyTarget } from '../../utils/learningGoalProgress';
import { mergeTopicProgressMaps } from '../../utils/studentProfileStorage';
import {
  isAllowedStudentAvatarUrl,
  isExplicitStudentAvatarChoice,
  normalizeStudentAvatarUrl,
} from '../../data/studentProfileAvatars';
import {
  DEFAULT_PRIMARY_SCHOOL_SLUG,
  isPrimarySchoolSlug,
  resolvePrimarySchoolSlug,
} from 'utils/primarySchool';
import {
  DEFAULT_THEME_PREFERENCE,
  isThemePreference,
  locationFromDbRow,
  locationToDbFields,
  resolveThemePreference,
  DEFAULT_LANGUAGE_PREFERENCE,
  isLanguagePreference,
  resolveLanguagePreference,
} from '../../utils/userProfilePreferences';

const STUDENT_PROFILE_COLUMNS =
  'id, display_name, avatar_url, student_level, interests, daily_goal_minutes, primary_school_slug, theme_preference, language_preference, location_label, location_lat, location_lng, location_source, onboarding_completed_at';
const JOURNEY_STATE_COLUMNS =
  'user_id, last_topic_key, current_streak, longest_streak, last_study_date, total_study_minutes';
const TOPIC_PROGRESS_COLUMNS =
  'user_id, topic_key, status, scroll_y, time_spent_seconds, last_visited_at, completed_at, started_at';
const HIGHLIGHT_COLUMNS =
  'id, user_id, topic_key, highlighted_text, note, color, in_revision_queue, created_at';
const BOOKMARK_COLUMNS = 'user_id, topic_key, created_at';
const LEARNING_GOAL_COLUMNS =
  'id, user_id, goal_type, title, title_hi, target_topic_keys, target_count, target_minutes_per_day, deadline, completed, created_at';
const DAILY_STUDY_COLUMNS = 'user_id, study_date, minutes';
const RECENT_TOPIC_COLUMNS = 'user_id, topic_key, visited_at';

function createEmptyProfileState() {
  return {
    version: 1,
    profile: {
      displayName: '',
      avatarUrl: null,
      studentLevel: 'beginner',
      interests: [],
      dailyGoalMinutes: 20,
      primarySchoolSlug: DEFAULT_PRIMARY_SCHOOL_SLUG,
      themePreference: DEFAULT_THEME_PREFERENCE,
      languagePreference: DEFAULT_LANGUAGE_PREFERENCE,
      location: null,
      onboardingCompletedAt: null,
    },
    topicProgress: {},
    highlights: [],
    bookmarks: [],
    goals: [],
    analytics: { dailyMinutes: {}, lastStudyDate: null, currentStreak: 0, longestStreak: 0 },
    recentTopics: [],
  };
}

function mapStatusFromDb(status) {
  if (status === 'revised') return 'in_progress';
  if (status === 'completed' || status === 'in_progress' || status === 'not_started') return status;
  return 'not_started';
}

/** Local-only Committer tier; DB enum uses beginner/intermediate/advanced. */
function studentLevelToDb(level) {
  if (level === 'starter') return 'beginner';
  if (level === 'beginner' || level === 'intermediate' || level === 'advanced') return level;
  return 'beginner';
}

/** @param {Record<string, unknown> | null} row @returns {StudentProfileState['topicProgress'][string]} */
function topicRowToLocal(row) {
  return {
    status: mapStatusFromDb(row.status),
    scrollY: row.scroll_y ?? 0,
    timeSpentSeconds: row.time_spent_seconds ?? 0,
    lastVisitedAt: row.last_visited_at ?? undefined,
    completedAt: row.completed_at ?? undefined,
  };
}

/**
 * Save nickname / display name only (lightweight - not blocked by full profile sync).
 * @param {string} userId
 * @param {string | null | undefined} displayName
 */
export async function pushStudentDisplayNameToSupabase(userId, displayName) {
  if (!isSupabaseConfigured()) return;

  const client = getSupabase();
  if (!client) return;

  const trimmed = displayName?.trim() ?? '';
  const payload = { display_name: trimmed || null };

  const { data: updated, error: updateError } = await client
    .from(LEARNER_TABLES.studentProfiles)
    .update(payload)
    .eq('id', userId)
    .select('id')
    .maybeSingle();

  if (updateError) throw updateError;
  if (updated) return;

  const { error: insertError } = await client.from(LEARNER_TABLES.studentProfiles).insert({
    id: userId,
    ...payload,
    student_level: 'beginner',
    daily_goal_minutes: 20,
    interests: [],
  });

  if (insertError) throw insertError;
}

/**
 * Save avatar URL only (lightweight - not blocked by full profile sync).
 * @param {string} userId
 * @param {string | null | undefined} avatarUrl
 */
export async function pushStudentAvatarToSupabase(userId, avatarUrl) {
  if (!isSupabaseConfigured()) return;

  const client = getSupabase();
  if (!client) return;

  const trimmed = avatarUrl?.trim() ? normalizeStudentAvatarUrl(avatarUrl.trim()) : '';
  if (trimmed && !isAllowedStudentAvatarUrl(trimmed)) {
    throw new Error('Invalid avatar selection.');
  }
  const payload = { avatar_url: trimmed || null };

  const { data: updated, error: updateError } = await client
    .from(LEARNER_TABLES.studentProfiles)
    .update(payload)
    .eq('id', userId)
    .select('id')
    .maybeSingle();

  if (updateError) throw updateError;
  if (updated) return;

  const { error: insertError } = await client.from(LEARNER_TABLES.studentProfiles).insert({
    id: userId,
    ...payload,
    student_level: 'beginner',
    daily_goal_minutes: 20,
    interests: [],
  });

  if (insertError) throw insertError;
}

/**
 * Load full student profile from Supabase for the signed-in user.
 * @param {string} userId
 * @returns {Promise<StudentProfileState>}
 */
export async function fetchStudentProfileFromSupabase(userId) {
  const base = createEmptyProfileState();
  if (!isSupabaseConfigured()) return base;

  const client = getSupabase();
  if (!client) return base;

  const [
    profileRes,
    journeyRes,
    progressRes,
    highlightsRes,
    bookmarksRes,
    goalsRes,
    dailyRes,
    recentRes,
  ] = await Promise.all([
    client.from(LEARNER_TABLES.studentProfiles).select(STUDENT_PROFILE_COLUMNS).eq('id', userId).maybeSingle(),
    client.from(LEARNER_TABLES.userJourneyState).select(JOURNEY_STATE_COLUMNS).eq('user_id', userId).maybeSingle(),
    client.from(LEARNER_TABLES.userTopicProgress).select(TOPIC_PROGRESS_COLUMNS).eq('user_id', userId),
    client.from(LEARNER_TABLES.userHighlights).select(HIGHLIGHT_COLUMNS).eq('user_id', userId).order('created_at', { ascending: false }),
    client.from(LEARNER_TABLES.userBookmarks).select(BOOKMARK_COLUMNS).eq('user_id', userId).order('created_at', { ascending: false }),
    client.from(LEARNER_TABLES.userLearningGoals).select(LEARNING_GOAL_COLUMNS).eq('user_id', userId).order('created_at', { ascending: false }),
    client.from(LEARNER_TABLES.userDailyStudy).select(DAILY_STUDY_COLUMNS).eq('user_id', userId),
    client.from(LEARNER_TABLES.userRecentTopics).select(RECENT_TOPIC_COLUMNS).eq('user_id', userId).order('visited_at', { ascending: false }),
  ]);

  if (profileRes.error) throw profileRes.error;
  if (journeyRes.error) throw journeyRes.error;
  if (progressRes.error) throw progressRes.error;
  if (highlightsRes.error) throw highlightsRes.error;
  if (bookmarksRes.error) throw bookmarksRes.error;
  if (goalsRes.error) throw goalsRes.error;
  if (dailyRes.error) throw dailyRes.error;
  if (recentRes.error) throw recentRes.error;

  const profileRow = profileRes.data;
  const journeyRow = journeyRes.data;

  /** @type {Record<string, StudentProfileState['topicProgress'][string]>} */
  const topicProgress = {};
  for (const row of progressRes.data ?? []) {
    topicProgress[row.topic_key] = topicRowToLocal(row);
  }

  const highlights = (highlightsRes.data ?? []).map((row) => ({
    id: row.id,
    topicId: row.topic_key,
    text: row.highlighted_text,
    note: row.note ?? '',
    color: row.color ?? 'amber',
    createdAt: row.created_at,
    inRevisionQueue: Boolean(row.in_revision_queue),
  }));

  const bookmarks = (bookmarksRes.data ?? []).map((row) => ({
    topicId: row.topic_key,
    addedAt: row.created_at,
  }));

  const goals = (goalsRes.data ?? []).map((row) => ({
    id: row.id,
    goalType: row.goal_type ?? (row.target_topic_keys?.length ? 'topics' : row.target_minutes_per_day ? 'minutes' : 'custom'),
    title: row.title,
    titleHi: row.title_hi ?? undefined,
    targetTopicIds: row.target_topic_keys ?? [],
    targetCount: row.target_count ?? undefined,
    targetMinutesPerDay: row.target_minutes_per_day ?? undefined,
    deadline: row.deadline ?? undefined,
    completed: Boolean(row.completed),
    createdAt: row.created_at,
  }));

  /** @type {Record<string, number>} */
  const dailyMinutes = {};
  for (const row of dailyRes.data ?? []) {
    dailyMinutes[row.study_date] = Number(row.minutes ?? 0);
  }

  const recentTopics = (recentRes.data ?? []).map((row) => ({
    topicId: row.topic_key,
    visitedAt: row.visited_at,
  }));

  return {
    ...base,
    profile: {
      displayName: profileRow?.display_name ?? '',
      avatarUrl: profileRow?.avatar_url ?? null,
      studentLevel: profileRow?.student_level ?? 'beginner',
      interests: profileRow?.interests ?? [],
      dailyGoalMinutes: profileRow?.daily_goal_minutes ?? 20,
      primarySchoolSlug: resolvePrimarySchoolSlug({
        primarySchoolSlug: profileRow?.primary_school_slug,
      }),
      themePreference: isThemePreference(profileRow?.theme_preference)
        ? profileRow.theme_preference
        : DEFAULT_THEME_PREFERENCE,
      languagePreference: isLanguagePreference(profileRow?.language_preference)
        ? profileRow.language_preference
        : DEFAULT_LANGUAGE_PREFERENCE,
      location: locationFromDbRow(profileRow),
      onboardingCompletedAt: profileRow?.onboarding_completed_at ?? null,
    },
    topicProgress,
    highlights,
    bookmarks,
    goals,
    analytics: {
      dailyMinutes,
      lastStudyDate: journeyRow?.last_study_date ?? null,
      currentStreak: journeyRow?.current_streak ?? 0,
      longestStreak: journeyRow?.longest_streak ?? 0,
    },
    recentTopics,
  };
}

/**
 * Push local student profile state to Supabase (upserts).
 * @param {string} userId
 * @param {StudentProfileState} state
 */
export async function pushStudentProfileToSupabase(userId, state) {
  if (!isSupabaseConfigured()) return;

  const client = getSupabase();
  if (!client) return;

  const { profile, topicProgress, highlights, bookmarks, goals, analytics, recentTopics } = state;

  const profileUpsert = client.from(LEARNER_TABLES.studentProfiles).upsert(
    buildStudentProfileUpsertRow(userId, profile, calculateDailyStudyTarget(state)),
    { onConflict: 'id' }
  );

  const journeyUpsert = client.from(LEARNER_TABLES.userJourneyState).upsert({
    user_id: userId,
    last_topic_key: recentTopics[0]?.topicId ?? null,
    current_streak: analytics.currentStreak ?? 0,
    longest_streak: analytics.longestStreak ?? 0,
    last_study_date: analytics.lastStudyDate,
    total_study_minutes: Math.round(Object.values(analytics.dailyMinutes ?? {}).reduce((s, m) => s + m, 0)),
  });

  const progressRows = Object.entries(topicProgress).map(([topicKey, p]) => ({
    user_id: userId,
    topic_key: topicKey,
    status: p.status ?? 'not_started',
    scroll_y: p.scrollY ?? 0,
    time_spent_seconds: p.timeSpentSeconds ?? 0,
    last_visited_at: p.lastVisitedAt ?? null,
    completed_at: p.completedAt ?? null,
    started_at: p.lastVisitedAt ?? null,
  }));

  const progressUpsert =
    progressRows.length > 0
      ? client.from(LEARNER_TABLES.userTopicProgress).upsert(progressRows, { onConflict: 'user_id,topic_key' })
      : Promise.resolve({ error: null });

  const highlightRows = highlights.map((h) => ({
    id: h.id,
    user_id: userId,
    topic_key: h.topicId,
    highlighted_text: h.text,
    note: h.note ?? '',
    color: h.color ?? 'amber',
    in_revision_queue: Boolean(h.inRevisionQueue),
    created_at: h.createdAt,
  }));

  const existingHighlights = await client.from(LEARNER_TABLES.userHighlights).select('id').eq('user_id', userId);
  if (existingHighlights.error) throw existingHighlights.error;
  const keepHighlightIds = new Set(highlights.map((h) => h.id));
  const deleteHighlightIds = (existingHighlights.data ?? [])
    .map((r) => r.id)
    .filter((id) => !keepHighlightIds.has(id));

  const highlightsUpsert =
    highlightRows.length > 0
      ? client.from(LEARNER_TABLES.userHighlights).upsert(highlightRows)
      : Promise.resolve({ error: null });

  const highlightsDelete =
    deleteHighlightIds.length > 0
      ? client.from(LEARNER_TABLES.userHighlights).delete().in('id', deleteHighlightIds)
      : Promise.resolve({ error: null });

  const bookmarkRows = bookmarks.map((b) => ({
    user_id: userId,
    topic_key: b.topicId,
    created_at: b.addedAt,
  }));

  const existingBookmarks = await client.from(LEARNER_TABLES.userBookmarks).select('topic_key').eq('user_id', userId);
  if (existingBookmarks.error) throw existingBookmarks.error;
  const keepBookmarkKeys = new Set(bookmarks.map((b) => b.topicId));
  const deleteBookmarkKeys = (existingBookmarks.data ?? [])
    .map((r) => r.topic_key)
    .filter((key) => !keepBookmarkKeys.has(key));

  await client.from(LEARNER_TABLES.userBookmarks).delete().eq('user_id', userId);
  const bookmarksInsert =
    bookmarkRows.length > 0
      ? client.from(LEARNER_TABLES.userBookmarks).insert(bookmarkRows)
      : Promise.resolve({ error: null });

  const goalRows = goals.map((g) => ({
    id: g.id,
    user_id: userId,
    goal_type: g.goalType ?? (g.targetTopicIds?.length ? 'topics' : g.targetMinutesPerDay ? 'minutes' : 'custom'),
    title: g.title,
    title_hi: g.titleHi ?? null,
    target_topic_keys: g.targetTopicIds ?? [],
    target_count: g.targetCount ?? null,
    target_minutes_per_day: g.targetMinutesPerDay ?? null,
    deadline: g.deadline ?? null,
    completed: Boolean(g.completed),
    created_at: g.createdAt,
  }));

  const existingGoals = await client.from(LEARNER_TABLES.userLearningGoals).select('id').eq('user_id', userId);
  if (existingGoals.error) throw existingGoals.error;
  const keepGoalIds = new Set(goals.map((g) => g.id));
  const deleteGoalIds = (existingGoals.data ?? []).map((r) => r.id).filter((id) => !keepGoalIds.has(id));

  const goalsUpsert =
    goalRows.length > 0
      ? client.from(LEARNER_TABLES.userLearningGoals).upsert(goalRows)
      : Promise.resolve({ error: null });

  const goalsDelete =
    deleteGoalIds.length > 0
      ? client.from(LEARNER_TABLES.userLearningGoals).delete().in('id', deleteGoalIds)
      : Promise.resolve({ error: null });

  const dailyRows = Object.entries(analytics.dailyMinutes ?? {}).map(([studyDate, minutes]) => ({
    user_id: userId,
    study_date: studyDate,
    minutes: Math.round(Number(minutes) || 0),
  }));

  const dailyUpsert =
    dailyRows.length > 0
      ? client.from(LEARNER_TABLES.userDailyStudy).upsert(dailyRows, { onConflict: 'user_id,study_date' })
      : Promise.resolve({ error: null });

  await client.from(LEARNER_TABLES.userRecentTopics).delete().eq('user_id', userId);
  const recentRows = recentTopics.map((r) => ({
    user_id: userId,
    topic_key: r.topicId,
    visited_at: r.visitedAt,
  }));
  const recentInsert =
    recentRows.length > 0
      ? client.from(LEARNER_TABLES.userRecentTopics).insert(recentRows)
      : Promise.resolve({ error: null });

  const results = await Promise.all([
    profileUpsert,
    journeyUpsert,
    progressUpsert,
    highlightsUpsert,
    highlightsDelete,
    bookmarksInsert,
    goalsUpsert,
    goalsDelete,
    dailyUpsert,
    recentInsert,
  ]);

  const err = results.find((r) => r?.error)?.error;
  if (err) throw err;

  if (deleteBookmarkKeys.length > 0) {
    /* bookmarks fully replaced via delete+insert above */
  }
}

/** Prefer local nickname when set - cloud may still hold OAuth/signup full name. */
function mergeDisplayName(localName, cloudName) {
  const local = localName?.trim() ?? '';
  const cloud = cloudName?.trim() ?? '';
  return local || cloud;
}

/** Prefer explicit local avatar; cloud wins over placeholder defaults in local cache. */
function mergeAvatarUrl(localUrl, cloudUrl) {
  const local = localUrl?.trim() ? normalizeStudentAvatarUrl(localUrl.trim()) : '';
  const cloud = cloudUrl?.trim() ? normalizeStudentAvatarUrl(cloudUrl.trim()) : '';
  if (isExplicitStudentAvatarChoice(local)) return local;
  if (cloud && isAllowedStudentAvatarUrl(cloud)) return cloud;
  if (local && isAllowedStudentAvatarUrl(local)) return local;
  return null;
}

/**
 * Profile row for full-state upsert. Omits display_name / avatar_url when local is empty
 * so background sync (topic progress, etc.) does not wipe cloud-only nickname or avatar.
 * Intentional clears use pushStudentDisplayNameToSupabase / pushStudentAvatarToSupabase.
 * @param {string} userId
 * @param {StudentProfileState['profile']} profile
 * @param {number} dailyGoalMinutes
 */
function buildStudentProfileUpsertRow(userId, profile, dailyGoalMinutes) {
  /** @type {Record<string, unknown>} */
  const row = {
    id: userId,
    student_level: studentLevelToDb(profile.studentLevel),
    daily_goal_minutes: dailyGoalMinutes,
    interests: profile.interests ?? [],
    primary_school_slug: isPrimarySchoolSlug(profile.primarySchoolSlug)
      ? profile.primarySchoolSlug
      : DEFAULT_PRIMARY_SCHOOL_SLUG,
    theme_preference: resolveThemePreference(profile),
    language_preference: resolveLanguagePreference(profile),
    ...locationToDbFields(profile.location),
  };

  if (profile.onboardingCompletedAt) {
    row.onboarding_completed_at = profile.onboardingCompletedAt;
  }

  const displayName = profile.displayName?.trim() ?? '';
  if (displayName) {
    row.display_name = displayName;
  }

  const avatarUrl = profile.avatarUrl?.trim() ? normalizeStudentAvatarUrl(profile.avatarUrl.trim()) : '';
  if (avatarUrl && isAllowedStudentAvatarUrl(avatarUrl)) {
    row.avatar_url = avatarUrl;
  }

  return row;
}

/** Merge cloud + local - keep unsynced local edits (e.g. deletions) instead of overwriting from cloud. */
export function mergeStudentProfileStates(cloud, local) {
  const merged = { ...cloud };
  if (!local) return merged;

  const cloudEmpty =
    Object.keys(cloud.topicProgress).length === 0 &&
    cloud.highlights.length === 0 &&
    cloud.bookmarks.length === 0;

  if (cloudEmpty) {
    return {
      ...local,
      profile: {
        ...local.profile,
        displayName: mergeDisplayName(local.profile.displayName, cloud.profile.displayName),
        avatarUrl: mergeAvatarUrl(local.profile.avatarUrl, cloud.profile.avatarUrl),
      },
    };
  }

  const localPending =
    Boolean(local.updatedAt) && (!local.lastSyncedAt || local.updatedAt > local.lastSyncedAt);

  if (localPending) {
    return {
      ...cloud,
      highlights: local.highlights,
      bookmarks: local.bookmarks,
      goals: local.goals,
      topicProgress: mergeTopicProgressMaps(cloud.topicProgress, local.topicProgress),
      analytics: {
        ...cloud.analytics,
        dailyMinutes: { ...cloud.analytics.dailyMinutes, ...local.analytics.dailyMinutes },
        currentStreak: Math.max(cloud.analytics.currentStreak ?? 0, local.analytics.currentStreak ?? 0),
        longestStreak: Math.max(cloud.analytics.longestStreak ?? 0, local.analytics.longestStreak ?? 0),
        lastStudyDate: local.analytics.lastStudyDate ?? cloud.analytics.lastStudyDate,
      },
      recentTopics: local.recentTopics?.length ? local.recentTopics : cloud.recentTopics,
      profile: {
        ...cloud.profile,
        ...local.profile,
        displayName: mergeDisplayName(local.profile.displayName, cloud.profile.displayName),
        avatarUrl: mergeAvatarUrl(local.profile.avatarUrl, cloud.profile.avatarUrl),
      },
      updatedAt: local.updatedAt,
      lastSyncedAt: local.lastSyncedAt,
    };
  }

  return {
    ...cloud,
    topicProgress: mergeTopicProgressMaps(cloud.topicProgress, local.topicProgress),
    profile: {
      ...cloud.profile,
      displayName: mergeDisplayName(local.profile.displayName, cloud.profile.displayName),
      avatarUrl: mergeAvatarUrl(local.profile.avatarUrl, cloud.profile.avatarUrl),
    },
    lastSyncedAt: local.lastSyncedAt ?? cloud.lastSyncedAt,
    updatedAt: local.updatedAt ?? cloud.updatedAt,
  };
}
